"use client";

import { parseGenres } from "@workspace/types";
import { cn } from "@workspace/ui/lib/utils";
import { memo } from "react";
import { GenreBadge } from "@/components/shared/genre-badge";

export interface GenreBadgeListProps {
	genres: string[];
	/**
	 * Maximum number of genres to display.
	 * Default: 3
	 */
	maxDisplay?: number;
	/** Class name for the wrapper div */
	className?: string;
	/** Class name for individual genre badges */
	badgeClassName?: string;
}

/**
 * A component for displaying arrays of genres with consistent behavior.
 * Handles empty/null genre arrays, applies parseGenres utility internally.
 * Shows max 3 genres by default.
 */
export const GenreBadgeList = memo<GenreBadgeListProps>(
	function GenreBadgeList({
		genres,
		maxDisplay = 3,
		className,
		badgeClassName,
	}) {
		// Handle null/empty arrays
		if (!genres || genres.length === 0) {
			return null;
		}

		const displayGenres = genres.slice(0, maxDisplay);

		return (
			<div className={cn("flex flex-wrap gap-1", className)}>
				{displayGenres.map((genre, index) => (
					<GenreBadge
						key={`${genre}-${index}`}
						genre={genre}
						className={badgeClassName}
					/>
				))}
			</div>
		);
	},
);

export interface GenreBadgeListFromDataProps
	extends Omit<GenreBadgeListProps, "genres"> {
	spotifyGenres: string[] | string | null;
	manualGenres?: string[] | string | null;
}

/**
 * A variant of GenreBadgeList that accepts raw genre data and applies parseGenres.
 * Use this when you have spotify_genres and manual_genres directly from the database.
 */
export const GenreBadgeListFromData = memo<GenreBadgeListFromDataProps>(
	function GenreBadgeListFromData({ spotifyGenres, manualGenres, ...props }) {
		const genres = parseGenres(spotifyGenres, manualGenres);
		return <GenreBadgeList genres={genres} {...props} />;
	},
);
